import {Injectable} from '@angular/core';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import {GatewayValues} from '@models/adminstration/gateway/gateway-configuration/gateway-values';
import {AdcSetting} from '@models/adminstration/gateway/gateway-configuration/adc-setting';
import {ModbusSetting} from '@models/adminstration/gateway/gateway-configuration/modbus-setting';
import {ADC_TYPE_OF_DATA, MODBUS_TYPE_OF_DATA} from '@constants/administration/gateway/gateway-constants';
import {AdcValue} from '@models/adminstration/gateway/gateway-configuration/adc-value';

@Injectable({
  providedIn: 'root'
})
export class GatewayImportExportService {

  private FIELDS_TO_REMOVE = ['digit4mA', 'digit20mA'];

  public createFilename(config: GatewaySignals) {
    const filename = 'cloudplug_' + config.parentId + '_connector_' + config.typeOfData;
    return this.addTimeStampToFilename(filename);
  }

  public createAdcFileName(gatewayId: string) {
    const filename = 'cloudplug_' + gatewayId + '_connector_' + ADC_TYPE_OF_DATA;
    return this.addTimeStampToFilename(filename);
  }

  public createModbusFilename(gatewayId: string) {
    const filename = 'cloudplug_' + gatewayId + '_connector_' + MODBUS_TYPE_OF_DATA;
    return this.addTimeStampToFilename(filename);
  }

  public generateExportDetails(config: GatewaySignals) {
    const details = {
      typeOfData: config.typeOfData,
      cloudplug: config.parentId,
      alias: config.alias,
      values: this.parseValuesAndDeleteNullFields(config.values, this.FIELDS_TO_REMOVE)
    };
    return JSON.stringify(details);
  }

  public generateAdcExportDetails(gatewayId: string, settings: AdcSetting) {
    const details = {
      typeOfData: ADC_TYPE_OF_DATA,
      cloudplug: gatewayId,
      sampleRateMs: settings.sampleRateMs,
      values: settings.values
    };
    return JSON.stringify(details);
  }

  public generateModbusExportDetails(gatewayId: string, settings: ModbusSetting) {
    const details = {
      typeOfData: MODBUS_TYPE_OF_DATA,
      cloudplug: gatewayId,
      polltimeMs: settings.polltimeMs,
      serialPort: settings.serialPort,
      slaves: settings.slaves
    };
    return JSON.stringify(details);
  }

  public getDuplicatesForConnectorConfig(config: any, field: string) {
    const values = config.values;
    if (!this.isValid(values)) {
      return {};
    }
    return this.generateMapOfDuplicates(values, field);
  }

  public generateDuplicatesValueEnumeration(duplicatesMap: any) {
    let result = '';

    if (!duplicatesMap) {
      return '';
    }
    const objects = Object.keys(duplicatesMap);
    for (const obj of objects) {
      if (duplicatesMap[obj].count > 1) {
        result += obj + ', ';
      }
    }

    result = result.slice(0, -2);
    return result;
  }

  public findSignalByName(oldSignals: AdcValue[], newSignalName: string) {
    for (const oldSignal of oldSignals) {
      if (oldSignal.name === newSignalName) {
        return oldSignal;
      }
    }
    return null;
  }

  public onFileSelected(event: Event) {
    if (!event.target) {
      return null;
    }
    const target = event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return null;
    }
    const file = target.files[0];
    if (!file) {
      return null;
    }
    return file;
  }


  private parseValuesAndDeleteNullFields(values: GatewayValues[], fieldsToRemove: string[]) {
    for (const val of values) {
      this.deleteFieldsIfNull(val, fieldsToRemove);
    }
    return values;
  }

  private deleteFieldsIfNull(value: GatewayValues, fieldsToRemove: string[]) {
    for (const field of fieldsToRemove) {
      if (value[field] === null) {
        delete value[field];
      }
    }
  }

  private formatDate(date: Date) {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 101).toString().substring(1);
    const day = (date.getDate() + 100).toString().substring(1);

    const hour = (date.getHours() + 100).toString().substring(1);
    const minute = (date.getMinutes() + 100).toString().substring(1);
    const second = (date.getSeconds() + 100).toString().substring(1);
    return year + month + day + '_' + hour + minute + second;
  }

  private addTimeStampToFilename(filename: string) {
    const timestamp = this.formatDate(new Date());
    return filename + '_' + timestamp + '_details.json';
  }

  private isValid(value: any) {
    if (value === null) {
      return false;
    }
    return value.length !== 0;
  }

  private generateMapOfDuplicates(values: any, field: string) {
    const duplicatesMap = {};
    for (const val of values) {
      const currentValue = val;
      let key = '';
      const fieldValue = currentValue[field];
      if (fieldValue !== null) {
        key = currentValue[field].toString().toUpperCase();
      }
      this.checkIfKeyIsValid(key, duplicatesMap, currentValue);
    }
    return this.filterOutNonDuplicatedItems(duplicatesMap);
  }

  private checkIfKeyIsValid(key: string, duplicatesMap: Record<string, any>, currentValue: any) {
    if (this.isValid(key)) {
      if (duplicatesMap[key] === null || !duplicatesMap.hasOwnProperty(key)) {
        duplicatesMap[key] = {};
        duplicatesMap[key].count = 1;
        duplicatesMap[key].active = currentValue.active ? 1 : 0;
      } else {
        duplicatesMap[key].count += 1;
        if (currentValue.active) {
          duplicatesMap[key].active += 1;
        }
      }
    }
  }

  private filterOutNonDuplicatedItems(map) {
    if (!map) {
      return {};
    }
    const objects = Object.keys(map);
    for (const obj of objects) {
      if (map[obj].count <= 1) {
        delete map[obj];
      }
    }
    return map;
  }

}
